import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Calendar, 
  DollarSign, 
  Ticket,
  Shield,
  Search,
  CheckCircle,
  XCircle,
  Ban,
  UserCheck,
  MoreVertical,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!authLoading && isAuthenticated && !isAdmin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchAdminData();
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, eventsRes, transRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/users?limit=100`),
        axios.get(`${API_URL}/admin/events?limit=50`),
        axios.get(`${API_URL}/admin/transactions?limit=50`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setEvents(eventsRes.data.events);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, verify = true) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/${verify ? 'verify' : 'unverify'}`);
      toast.success(verify ? 'User verified' : 'User unverified');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleSuspendUser = async (userId, suspend = true) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/${suspend ? 'suspend' : 'unsuspend'}`);
      toast.success(suspend ? 'User suspended' : 'User unsuspended');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`User role changed to ${newRole}`);
      fetchAdminData();
      setShowUserModal(false);
    } catch (error) {
      toast.error('Failed to change role');
    }
  };

  const handleChangeEventStatus = async (eventId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/events/${eventId}/status`, { status: newStatus });
      toast.success(`Event status changed to ${newStatus}`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.username?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (authLoading || !isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl">Admin Panel</h1>
            <p className="text-muted-foreground">Manage users, events, and platform settings</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
                <Users className="w-5 h-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold">{stats.users.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.users.fans} fans, {stats.users.influencers} influencers
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Events</CardTitle>
                <Calendar className="w-5 h-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold">{stats.events.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.events.published} published
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Tickets Sold</CardTitle>
                <Ticket className="w-5 h-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold">{stats.tickets.sold}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  all time
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Platform Revenue</CardTitle>
                <DollarSign className="w-5 h-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold text-green-500">
                  ${(stats.revenue.platform_commission + stats.revenue.resale_commission).toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  30% commission earned
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-card border border-white/10">
            <TabsTrigger value="users" data-testid="admin-users-tab">Users</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-events-tab">Events</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-transactions-tab">Transactions</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-users"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="fan">Fans</SelectItem>
                  <SelectItem value="influencer">Influencers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              {u.avatar_url ? (
                                <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-sm font-medium">{u.display_name?.charAt(0)}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{u.display_name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                              {u.username && <p className="text-xs text-primary">{u.username}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="capitalize">{u.role}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {u.verified && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                            {u.suspended && (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                <Ban className="w-3 h-3 mr-1" />
                                Suspended
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            {u.role === 'influencer' && (
                              <Button
                                size="sm"
                                variant={u.verified ? 'outline' : 'default'}
                                onClick={() => handleVerifyUser(u.id, !u.verified)}
                                data-testid={`verify-user-${u.id}`}
                              >
                                {u.verified ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant={u.suspended ? 'default' : 'destructive'}
                              onClick={() => handleSuspendUser(u.id, !u.suspended)}
                              data-testid={`suspend-user-${u.id}`}
                            >
                              {u.suspended ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedUser(u); setShowUserModal(true); }}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Event</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Creator</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Tickets</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {events.map((event) => (
                      <tr key={event.id} className="hover:bg-white/5" data-testid={`event-row-${event.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{event.title}</p>
                            <p className="text-sm text-muted-foreground capitalize">{event.category}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{event.influencer_name}</td>
                        <td className="px-4 py-3">
                          <Badge 
                            className={
                              event.status === 'published' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                              event.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                              event.status === 'paused' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                              'bg-gray-500/20 text-gray-400 border-gray-500/30'
                            }
                          >
                            {event.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {event.sold_tickets}/{event.total_tickets}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Select
                              value={event.status}
                              onValueChange={(value) => handleChangeEventStatus(event.id, value)}
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="paused">Paused</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Transaction</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Buyer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Commission</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{tx.event_title}</p>
                            <p className="text-sm text-muted-foreground">{tx.tier_name}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{tx.fan_name}</td>
                        <td className="px-4 py-3 font-medium">${tx.price_paid}</td>
                        <td className="px-4 py-3 text-green-400">${tx.commission?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3">
                          <Badge 
                            className={
                              tx.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                              tx.status === 'used' ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' :
                              'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                            }
                          >
                            {tx.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Details Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>Manage user role and permissions</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  {selectedUser.avatar_url ? (
                    <img src={selectedUser.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-xl font-medium">{selectedUser.display_name?.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg">{selectedUser.display_name}</p>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Change Role</label>
                <Select
                  value={selectedUser.role}
                  onValueChange={(value) => handleChangeRole(selectedUser.id, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fan">Fan</SelectItem>
                    <SelectItem value="influencer">Influencer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
